import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { LoadingSpinner } from "@/components/ui/spinner";
import { TypographyP, TypographySmall } from "@/components/ui/typography";

type Props = {
  userToken?: string;
  targetToken?: string;
  onSubmit: ({
    userToken,
    targetToken,
  }: {
    userToken: string;
    targetToken: string;
  }) => Promise<void>;
};

export const KanzleilandIntegrationStepOne = ({
  userToken: _userToken,
  targetToken: _targetToken,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [userToken, setUserToken] = useState(_userToken ?? "");
  const [targetToken, setTargetToken] = useState(_targetToken ?? "");

  useEffect(() => {
    setUserToken(_userToken ?? "");
    setTargetToken(_targetToken ?? "");
  }, [_userToken, _targetToken]);

  const handleUserTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserToken(e.target.value);
  };

  const handleTargetTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTargetToken(e.target.value);
  };

  const isValid = useMemo(() => {
    return userToken.length > 0 && targetToken.length > 0;
  }, [userToken, targetToken]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        userToken,
        targetToken,
      });
    } finally {
      setLoading(false);
    }
  };

  const isConnected = useMemo(() => {
    return _userToken && _targetToken;
  }, [_userToken, _targetToken]);

  return (
    <div className={"flex flex-col gap-2"}>
      <div className="flex flex-row gap-2 items-center">
        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
          <TypographyP className="text-muted-foreground">1</TypographyP>
        </div>
        <div className="flex flex-col gap-1">
          <TypographySmall>
            {t("integrations.kanzleiland.step_1")}
          </TypographySmall>
          <TypographySmall className="text-muted-foreground">
            {t("integrations.kanzleiland.step_1_description")}
          </TypographySmall>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex justify-center h-auto w-10">
          <Separator orientation="vertical" />
        </div>
        <div className="flex-1 mt-2">
          <form onSubmit={handleSubmit}>
            <div className="grid w-full items-center gap-4 mb-2">
              <div className="flex flex-col space-y-2">
                <Label htmlFor="taxAdvisorNumber">
                  {t("integrations.kanzleiland.user_token_label")}
                </Label>
                <TypographySmall className="text-muted-foreground">
                  {t("integrations.kanzleiland.user_token_description")}
                </TypographySmall>
                <Input
                  placeholder={t(
                    "integrations.kanzleiland.user_token_placeholder",
                  )}
                  value={userToken}
                  onChange={handleUserTokenChange}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <Label htmlFor="clientNumber">
                  {t("integrations.kanzleiland.target_token_label")}
                </Label>
                <TypographySmall className="text-muted-foreground">
                  {t("integrations.kanzleiland.target_token_description")}
                </TypographySmall>
                <Input
                  id="clientNumber"
                  placeholder={t(
                    "integrations.kanzleiland.target_token_placeholder",
                  )}
                  value={targetToken}
                  onChange={handleTargetTokenChange}
                />
              </div>
              {!isConnected && (
                <div className="flex justify-start">
                  <Button type="submit" className="gap-2" disabled={!isValid}>
                    {loading && <LoadingSpinner />}
                    {t("integrations.kanzleiland.connect")}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
